import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

@Injectable()
export class CalendarSearchService {
  termUpdate: Subject<string>;
  resultUpdate: Subject<CalendarSearchResult>;
  highlightUpdate: Subject<string>;

  private _term: string;
  private _highlightedIndex = 0;
  private _latestSearchResults: CalendarSearchResult = null;
  private results: [[string, boolean]];
  private matchesResults: string[] = [];
  private matches = 0;

  get term(): string {
    return this._term;
  }

  set term(newTerm: string) {
    this._term = newTerm;
    this.termUpdate.next(this._term);
  }

  get latestResults(): CalendarSearchResult {
    return this._latestSearchResults;
  }

  get currentItem(): string {
    return this.matchesResults &&
      this.matchesResults.length > 0 &&
      this._highlightedIndex > -1
      ? this.matchesResults[this._highlightedIndex]
      : undefined;
  }

  get currentHighlightedIndex(): number {
    return this._highlightedIndex;
  }

  constructor() {
    this.termUpdate = new Subject<string>();
    this.resultUpdate = new Subject<CalendarSearchResult>();
    this.highlightUpdate = new Subject<string>();
  }

  highlightPrevious() {
    if (
      !this.matchesResults[this._highlightedIndex] ||
      this._highlightedIndex === 0
    ) {
      this._highlightedIndex = this.matchesResults.length - 1;
    } else {
      this._highlightedIndex--;
    }
    this.highlightUpdate.next(this.matchesResults[this._highlightedIndex]);
  }

  highlightNext() {
    this._highlightedIndex++;
    if (!this.matchesResults[this._highlightedIndex]) {
      this._highlightedIndex = 0;
    }
    this.highlightUpdate.next(this.matchesResults[this._highlightedIndex]);
  }

  updateResult(itemId: string, found: boolean) {
    if (found) {
      this.matchesResults.push(itemId);
    }

    if (!this.results) {
      this.results = [[itemId, found]];
    } else {
      this.results.push([itemId, found]);
    }
  }

  resetResults() {
    this._highlightedIndex = 0;
    if (this.results) {
      this.results.splice(0, this.results.length);
    }
    if (this.matchesResults) {
      this.matchesResults.splice(0, this.matchesResults.length);
    }
    this.highlightUpdate.next("");
  }

  moveToSearchResult(index: number) {
    this._highlightedIndex = 0;
    this.matchesResults = this.matchesResults
      .slice(index)
      .concat(this.matchesResults.slice(0, index));
    this.highlightUpdate.next(this.matchesResults[this._highlightedIndex]);
  }

  updateObservers() {
    this._latestSearchResults = new CalendarSearchResult(
      this._term,
      this.results,
      this.matchesResults,
      this.matchesResults.length,
      this.termUpdate.observers.length,
    );
    this.resultUpdate.next(this._latestSearchResults);
  }

  testSearchTermMatch(haystack: string): boolean {
    if (this._term === "" || this._term === " ") {
      return false;
    }
    const regEx: RegExp = new RegExp(`(${this._term})`, "ig");
    return regEx.test(haystack);
  }

  highlightTerm(text: string, itemId: string): string {
    if (!this._term || this._term === "" || this._term === " ") {
      return text;
    }
    const highlight = this.matchesResults[this._highlightedIndex] === itemId;
    const regEx: RegExp = new RegExp(`(${this._term})`, "ig");
    const span = '<span class="search-highlight'
      .concat(highlight ? ' highlighted"' : '"')
      .concat(">$1</span>");
    return text.replace(regEx, span);
  }
}

export class CalendarSearchResult {
  constructor(
    public term: string,
    public results: [[string, boolean]],
    public resultsMatch: string[],
    public matches: number,
    public totalItems: number,
  ) {}
}
